/**
 * components/ui/AppReactSelect.js
 * ─────────────────────────────────────────────────────────────────────────────
 * react-select, dressed as the dashboard's dropdown.
 *
 * Searchable and async pickers (vendors, products, riders) still need
 * react-select's typing and loading, so they are not AppSelect. They should
 * still LOOK like AppSelect: same 36px control, 8px radius, #083860 focus
 * ring, the same menu card, hover and selected states. This wraps both
 * react-select flavours with those styles. A call site's own `styles` are
 * merged on top, so a width or z-index it set keeps working.
 *
 *   import Select from "../components/ui/AppReactSelect";
 *   import { AppAsyncSelect as AsyncSelect } from "../components/ui/AppReactSelect";
 */
import React, { forwardRef } from "react";
import Select, { mergeStyles } from "react-select";
import AsyncSelect from "react-select/async";

const NAVY = "#083860";

export const appSelectStyles = {
  control: (base, s) => ({
    ...base,
    minHeight: 36,
    borderRadius: 8,
    borderColor: s.isFocused ? NAVY : "#cbd5e1",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(8, 56, 96, 0.28)" : "0 1px 2px rgba(15, 23, 42, 0.04)",
    backgroundColor: s.isDisabled ? "#f8fafc" : "#ffffff",
    fontSize: 13,
    "&:hover": { borderColor: s.isFocused ? NAVY : "#94a3b8" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  placeholder: (base) => ({ ...base, color: "#94a3b8" }),
  singleValue: (base) => ({ ...base, color: "#0f172a" }),
  input: (base) => ({ ...base, color: "#0f172a" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, s) => ({
    ...base,
    padding: "0 8px",
    color: "#64748b",
    transition: "transform 0.15s",
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
    "&:hover": { color: "#334155" },
  }),
  clearIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", "&:hover": { color: "#334155" } }),
  menu: (base) => ({
    ...base,
    marginTop: 4,
    padding: 4,
    borderRadius: 10,
    border: "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.06)",
    fontSize: 13,
    zIndex: 30,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10060 }),
  menuList: (base) => ({ ...base, padding: 0 }),
  group: (base) => ({ ...base, padding: "4px 0" }),
  groupHeading: (base) => ({ ...base, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: "#94a3b8" }),
  option: (base, s) => ({
    ...base,
    borderRadius: 6,
    padding: "7px 10px",
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    backgroundColor: s.isSelected ? "#e6eef6" : s.isFocused ? "#f1f5f9" : "transparent",
    color: s.isDisabled ? "#94a3b8" : s.isSelected ? NAVY : "#0f172a",
    fontWeight: s.isSelected ? 600 : 400,
    ":active": { backgroundColor: "#e2e8f0" },
  }),
  multiValue: (base) => ({ ...base, borderRadius: 999, backgroundColor: "#e6eef6" }),
  multiValueLabel: (base) => ({ ...base, color: NAVY, fontSize: 12, padding: "2px 2px 2px 8px" }),
  multiValueRemove: (base) => ({ ...base, borderRadius: 999, color: NAVY, ":hover": { backgroundColor: "#c7d6e6", color: NAVY } }),
  noOptionsMessage: (base) => ({ ...base, fontSize: 12, color: "#64748b" }),
  loadingMessage: (base) => ({ ...base, fontSize: 12, color: "#64748b" }),
};

const withKit = (Component, displayName) => {
  const Wrapped = forwardRef(function KitSelect(props, ref) {
    const styles = props.styles ? mergeStyles(appSelectStyles, props.styles) : appSelectStyles;
    return <Component ref={ref} classNamePrefix="lk-rs" {...props} styles={styles} />;
  });
  Wrapped.displayName = displayName;
  return Wrapped;
};

export const AppAsyncSelect = withKit(AsyncSelect, "AppAsyncSelect");
const AppReactSelect = withKit(Select, "AppReactSelect");
export default AppReactSelect;
